/**
 * Bootstrap.js - Dependency Injection Container and Application Bootstrap
 * 
 * Centralizes ALL initialization logic:
 * - Model creation (state, instanceModel, definitionModel, connectionModel)
 * - Service creation (authService, storageService, favoritesService, etc.)
 * - Controller initialization (all initXController calls)
 * - Event listener setup
 * 
 * app.js contains ONLY:
 * - Runtime functions (renderDevice, createConnection, handlers)
 * - Business logic
 * - The actual application behavior
 */

// ============================================================================
// Imports
// ============================================================================

import { appState } from './services/AppState.js';
import { eventBus, EVENTS } from './services/EventBus.js';
import { historyService, initUndoManager } from './services/UndoManager.js';
import { createStorageService } from './services/StorageService.js';
import { createAuthService } from './services/AuthService.js';
import { createFavoritesService } from './services/FavoritesService.js';
import { createGlobalDevicesService } from './services/GlobalDevicesService.js';
import { createPatchSettingsService } from './services/PatchSettingsService.js';
import { createDeviceService } from './services/DeviceService.js';
import { recentPatchesService } from './services/RecentPatchesService.js';
import { createDeviceInstanceModel } from './models/DeviceInstanceModel.js';
import { createDeviceDefinitionModel } from './models/DeviceDefinitionModel.js';
import { createConnectionModel } from './models/ConnectionModel.js';
import { createConnectionRules } from './config/connectionRules.js';
import { showToast } from './controllers/ToastController.js';
import { deepClone } from './utils/helpers.js';

// Controller imports
import { initSelectionController, getLibraryMultiSelect, getSelectedDevices, getSingleSelectedDevice,
         setDeviceSelection, addToDeviceSelection, toggleDeviceSelection, clearDeviceSelection,
         hasDeviceSelection, updateSelectionVisuals, selectDevicesInBoundsRect,
         getBoxDragState, setBoxDragState, clearBoxSelection, handleToggleSelect,
         clearMultiSelection, getMultiSelectState } from './controllers/SelectionController.js';
import { initCableRenderer, renderAllCables, updateConnectedPorts, getPortPosition,
         getConnectionSourceGroup, getConnectionTargetGroup, createCablePath } from './controllers/CableRenderer.js';
import { initCableDragController, getCableDragState, isDraggingCable, getLastMousePosition,
         setLastMousePosition, getLastHoveredTargetPortInfo, updateTempCable } from './controllers/CableDragController.js';
import { initDragController, isDraggingDevice, bringToFront } from './controllers/DragController.js';
import { initCanvasEventController, handlePointerMove, handlePointerUp } from './controllers/CanvasEventController.js';
import { initGestureController, getPointerPos, getCanvasPanState, cancelLongPress,
         startLongPressTimer } from './controllers/GestureController.js';
import { initTouchController, cancelLibraryTouchDrag } from './controllers/TouchController.js';
import { initKeyboardController, getHeldWasdKey, WASD_SIDE_MAP } from './controllers/KeyboardController.js';
import { initKeyboardShortcuts } from './controllers/KeyboardShortcutsController.js';
import { initViewportController, getViewport, saveViewport, loadViewport, applyViewportTransform,
         screenToCanvas, zoomAtPoint, handleWheel, updateZoomIndicator, resetZoom,
         fitAllDevices, toggleFitPatch } from './controllers/ViewportController.js';
import { initModalController, showConfirmModal } from './controllers/ModalController.js';
import { initDeviceModalController, showAddDeviceModal, showEditDeviceModal,
         showEditTemplateModal, closeModal } from './controllers/DeviceModalController.js';
import { initContextMenuController, showContextMenu, contextMenuAction } from './controllers/ContextMenuController.js';
import { initDebugController, loadDebugSettings, toggleDebugPanel, updateDebugStraightDist,
         updateDebugCableStyle, updatePortRule, deletePortType, exportDebugSettings } from './controllers/DebugController.js';
import { initClipboardController, hasClipboardContent, getClipboardState, copyDevice, pasteDevice,
         duplicateDevice, copySelection, pasteSelection, copyDeviceToSystemClipboard,
         copyLibrarySelectionToClipboard, pasteDeviceFromSystemClipboard,
         copySelectionToSystemClipboard, pasteSelectionFromSystemClipboard,
         getSelectionClipboard, hasSelectionClipboard, performCopy, performCut, performPaste,
         copySelectedToClipboard, pasteFromClipboard, duplicateSelectedDevice,
         exportSelectedDevice, importDeviceFromFile } from './controllers/ClipboardController.js';
import { initDeletionController, deleteSelected, deleteSelectedDevices, deleteDevice,
         deleteConnection, deleteSelectedCables, clearRouting } from './controllers/DeletionController.js';
import { initAccountMenu, initPatchSwitcherMenu, initFileMenu, initUserMenu,
         toggleAccountMenu, closeAccountMenu, togglePatchSwitcher, closePatchSwitcher,
         toggleFileMenu, closeFileMenu, toggleUserMenu, closeUserMenu } from './controllers/MenuController.js';
import { initAuthUIController } from './controllers/AuthUIController.js';
import { initVersionHistoryController } from './controllers/VersionHistoryController.js';
import { initStorageUIController } from './controllers/StorageUIController.js';
import { initMigrationController } from './controllers/MigrationController.js';
import { initLibraryController } from './controllers/LibraryController.js';
import { initInstanceLayoutController } from './controllers/InstanceLayoutController.js';
import { initShareController } from './controllers/ShareController.js';
import { initPatchSwitcherController } from './controllers/PatchSwitcherController.js';
import { initPatchModalController } from './controllers/PatchModalController.js';
import { findOpenPosition } from './services/LayoutService.js';
import { APP_VERSION, BUILD_TIMESTAMP, CURRENT_PATCH_STORAGE_KEY } from './constants/index.js';

// ============================================================================
// Dependency Injection Container
// ============================================================================

class Container {
    #services = new Map();
    #initializing = new Set();
    
    register(name, factory, options = {}) {
        const { singleton = true } = options;
        this.#services.set(name, { factory, instance: null, singleton });
        return this;
    }
    
    get(name) {
        const service = this.#services.get(name);
        if (!service) throw new Error(`Service "${name}" not found in container`);
        
        if (service.singleton && service.instance !== null) return service.instance;
        
        if (this.#initializing.has(name)) {
            throw new Error(`Circular dependency detected for service "${name}"`);
        }
        
        this.#initializing.add(name);
        try {
            const instance = typeof service.factory === 'function' 
                ? service.factory(this) 
                : service.factory;
            if (service.singleton) service.instance = instance;
            return instance;
        } finally {
            this.#initializing.delete(name);
        }
    }
    
    has(name) { return this.#services.has(name); }
    getServiceNames() { return Array.from(this.#services.keys()); }
}

export const container = new Container();

// ============================================================================
// Bootstrap Function
// ============================================================================

/**
 * Bootstrap the application.
 * 
 * @param {Object} runtime - Runtime functions from app.js
 * @returns {Object} Initialized services and controllers for app.js to use
 */
export async function bootstrap(runtime) {
    console.log('[Bootstrap] Starting application bootstrap...');
    
    // =========================================================================
    // Phase 1: Core State & Models
    // =========================================================================
    
    // Initialize app state
    appState.set('patch.name', 'Untitled Patch');
    appState.set('auth.user', null);
    appState.set('selection.connectionId', null);
    appState.set('selection.templateId', null);
    appState.set('debug', { cableStraightDist: 10, cableStyle: 'curvy' });
    
    // Create reactive debug config proxy
    const debugConfig = {
        get cableStraightDist() { return appState.get('debug').cableStraightDist; },
        set cableStraightDist(val) { 
            const debug = appState.get('debug');
            appState.set('debug', { ...debug, cableStraightDist: val });
        },
        get cableStyle() { return appState.get('debug').cableStyle; },
        set cableStyle(val) { 
            const debug = appState.get('debug');
            appState.set('debug', { ...debug, cableStyle: val });
        }
    };
    
    // State object with arrays for models
    const state = {
        templates: [],
        devices: [],
        connections: [],
        get selectedConnectionId() { return appState.get('selection.connectionId'); },
        set selectedConnectionId(val) { appState.set('selection.connectionId', val); },
        get selectedTemplateId() { return appState.get('selection.templateId'); },
        set selectedTemplateId(val) { appState.set('selection.templateId', val); }
    };
    
    // Create models
    const instanceModel = createDeviceInstanceModel(state);
    const definitionModel = createDeviceDefinitionModel(state);
    const connectionModel = createConnectionModel(state);
    
    // Create mutable port connection rules
    let PORT_CONNECTION_RULES = createConnectionRules();
    
    // Register in container
    container.register('state', state);
    container.register('instanceModel', instanceModel);
    container.register('definitionModel', definitionModel);
    container.register('connectionModel', connectionModel);
    container.register('debugConfig', debugConfig);
    
    // =========================================================================
    // Phase 2: Services
    // =========================================================================
    
    const patchSettingsService = createPatchSettingsService();
    container.register('patchSettingsService', patchSettingsService);
    
    // Forward declarations for circular dependencies
    let syncUserDataFromCloud = null;
    
    // Auth service
    const authService = createAuthService({
        onAuthStateChange: (event, session, userProfile) => {
            appState.set('auth.user', session?.user || null);
            
            if (event === 'SIGNED_IN' && session) {
                eventBus.emit(EVENTS.AUTH_CHANGED, { isLoggedIn: true, user: session.user });
                if (syncUserDataFromCloud) syncUserDataFromCloud();
            } else if (event === 'SIGNED_OUT') {
                eventBus.emit(EVENTS.AUTH_CHANGED, { isLoggedIn: false, user: null });
                container.get('storageService')?.onLogout();
                recentPatchesService.resetCloudLoadedState();
            }
        },
        onError: (error) => showToast(error.message || 'Auth error', true)
    });
    container.register('authService', authService);
    
    // Storage service
    const storageService = createStorageService({
        authService,
        definitionModel,
        instanceModel,
        connectionModel,
        getPatchSettings: patchSettingsService.getPatchSettings
    });
    storageService.setAutosaveCallback(runtime.showAutosaveIndicator);
    container.register('storageService', storageService);
    
    // Favorites service
    const favoritesService = createFavoritesService();
    favoritesService.setAuthService(authService);
    container.register('favoritesService', favoritesService);
    
    // Global devices service
    const globalDevicesService = createGlobalDevicesService();
    globalDevicesService.setAuthService(authService);
    container.register('globalDevicesService', globalDevicesService);
    
    // Recent patches service setup
    recentPatchesService.setAuthService(authService);
    recentPatchesService.setStorageService(storageService);
    recentPatchesService.setOnErrorCallback((msg) => showToast(msg, true));
    
    // Cloud sync function
    syncUserDataFromCloud = async () => {
        try {
            await favoritesService.loadFromCloud();
            runtime.renderLibrary?.();
        } catch (e) {
            console.warn('Failed to sync favorites from cloud:', e);
        }
        try {
            await recentPatchesService.loadFromCloud();
        } catch (e) {
            console.warn('Failed to sync recents from cloud:', e);
        }
    };
    
    // =========================================================================
    // Phase 3: DOM References
    // =========================================================================
    
    const canvas = document.getElementById('canvas');
    const cablesSvg = document.getElementById('cables-svg');
    
    container.register('canvas', canvas);
    container.register('cablesSvg', cablesSvg);
    
    // =========================================================================
    // Phase 4: Selection State
    // =========================================================================
    
    const selectionState = {
        get devices() { return getSelectedDevices(); },
        get connections() { return [] }
    };
    container.register('selectionState', selectionState);
    
    // =========================================================================
    // Phase 5: Controller Initialization
    // =========================================================================
    
    // Selection controller (must be first - other controllers depend on it)
    initSelectionController({
        instanceModel, connectionModel, definitionModel, canvas, screenToCanvas,
        getPortPosition, getConnectionSourceGroup, getConnectionTargetGroup,
        cableIntersectsBoxGeom: runtime.cableIntersectsBoxGeom, debugConfig, state,
        renderLibrary: () => runtime.renderLibrary?.()
    });
    
    const libraryMultiSelect = getLibraryMultiSelect();
    container.register('libraryMultiSelect', libraryMultiSelect);
    
    // Cable renderer
    initCableRenderer({
        cablesSvg, canvas, instanceModel, connectionModel, definitionModel,
        getViewport, getTemplateForDevice: runtime.getTemplateForDevice, clearDeviceSelection,
        updateSelectionButtons: runtime.updateSelectionButtons,
        renderLibrary: () => runtime.renderLibrary?.(), state, debugConfig,
        getEffectiveCableStyle: patchSettingsService.getEffectiveCableStyle
    });
    
    // Cable drag controller
    initCableDragController({
        cablesSvg, instanceModel, connectionModel, screenToCanvas, getPortPosition,
        createCablePath, getTemplateForDevice: runtime.getTemplateForDevice,
        createConnection: runtime.createConnection, getConnectionSourceGroup, getConnectionTargetGroup,
        getPointerPos
    });
    
    // Drag controller
    initDragController({
        instanceModel, selectDevice: runtime.selectDevice, duplicateDevice,
        renderAllCables, scheduleAutosave: runtime.scheduleAutosave,
        recordAction: runtime.recordAction
    });
    
    // Canvas event controller
    initCanvasEventController({
        instanceModel, definitionModel, connectionModel, state,
        selectDevice: runtime.selectDevice, updateSelectionButtons: runtime.updateSelectionButtons,
        renderLibrary: () => runtime.renderLibrary?.(), screenToCanvas
    });
    
    // Clipboard controller
    initClipboardController({
        instanceModel, definitionModel, connectionModel,
        renderDevice: runtime.renderDevice, renderAllCables, updateConnectedPorts,
        renderLibrary: () => runtime.renderLibrary?.(), scheduleAutosave: runtime.scheduleAutosave,
        recordAction: runtime.recordAction, selectDevice: runtime.selectDevice,
        updateSelectionButtons: runtime.updateSelectionButtons,
        getTemplateForDevice: runtime.getTemplateForDevice,
        getConnectionSourceGroup, getConnectionTargetGroup,
        findOpenPosition: () => findOpenPosition(instanceModel),
        getSelectedTemplateId: () => state.selectedTemplateId,
        duplicateSelectedTemplate: () => runtime.duplicateSelectedTemplate?.(),
        selectionState
    });
    
    // Undo manager
    initUndoManager({
        instanceModel, definitionModel, connectionModel,
        renderDevice: runtime.renderDevice, renderAllCables, updateConnectedPorts,
        renderLibrary: () => runtime.renderLibrary?.(), scheduleAutosave: runtime.scheduleAutosave,
        selectionState, state, updateSelectionVisuals,
        updateSelectionButtons: runtime.updateSelectionButtons,
        clearDeviceSelection, deepClone, showToast
    });
    historyService.setUpdateCallback(runtime.updateUndoRedoButtons);
    
    // Gesture controller
    initGestureController({
        getViewport, zoomAtPoint, applyViewportTransform, updateZoomIndicator, renderAllCables,
        getBoxDragState, setBoxDragState, addToDeviceSelection, toggleDeviceSelection,
        updateSelectionVisuals, updateSelectionButtons: runtime.updateSelectionButtons,
        handleToggleSelect, clearMultiSelection, showContextMenu, cancelLibraryTouchDrag,
        triggerUndo: runtime.undo, triggerRedo: runtime.redo, toggleFitPatch, saveViewport
    });
    
    // Touch controller
    initTouchController({
        definitionModel, favoritesService, screenToCanvas,
        createInstanceFromTemplate: runtime.createInstanceFromTemplate,
        renderLibrary: () => runtime.renderLibrary?.(), startLongPressTimer, cancelLongPress,
        isDraggingDevice, isDraggingCable, handlePointerMove, handlePointerUp, getCanvasPanState
    });
    
    // Debug controller
    initDebugController({
        debugConfig, portConnectionRules: PORT_CONNECTION_RULES, renderAllCables, showToast
    });
    
    // Viewport controller
    initViewportController({ canvas, cablesSvg, renderAllCables, showToast, instanceModel });
    
    // Menu controllers
    initAccountMenu();
    initPatchSwitcherMenu({ onOpen: () => window.renderPatchSwitcherList?.() });
    initFileMenu();
    initUserMenu();
    initModalController();
    
    // Auth UI controller
    const authUIController = initAuthUIController({
        authService, closeUserMenu, closeAccountMenu
    });
    
    // Version history controller
    const versionHistoryController = initVersionHistoryController({
        storageService, loadPatch: runtime.loadVersion, showConfirmModal
    });
    
    // Storage UI controller
    const storageUIController = initStorageUIController({
        storageService, loadAndApplyPatch: runtime.loadAndApplyPatch,
        setCurrentPatchName: runtime.setCurrentPatchName, recentPatchesService,
        invalidatePatchesCache: () => patchSwitcherController?.invalidatePatchesCache(),
        autosave: runtime.autosave, showConfirmModal,
        getInstanceCount: () => instanceModel.getCount(),
        getConnectionCount: () => connectionModel.getCount(),
        getCurrentPatchName: () => appState.get('patch.name')
    });
    
    // Migration controller
    const migrationController = initMigrationController({
        authService, storageService, favoritesService, recentPatchesService
    });
    
    // Library controller
    const libraryController = initLibraryController({
        definitionModel, instanceModel, connectionModel, favoritesService, globalDevicesService, state,
        scheduleAutosave: runtime.scheduleAutosave, recordAction: runtime.recordAction,
        renderDevice: runtime.renderDevice, clearDeviceSelection,
        updateSelectionButtons: runtime.updateSelectionButtons,
        renderAllCables, updateConnectedPorts, screenToCanvas,
        createInstanceFromTemplate: runtime.createInstanceFromTemplate, getPointerPos,
        showEditTemplateModal, updateFooterLibraryState: runtime.updateFooterLibraryState
    });
    
    // Deletion controller
    initDeletionController({
        instanceModel, connectionModel, definitionModel, selectionState, state,
        clearDeviceSelection, updateSelectionVisuals,
        updateSelectionButtons: runtime.updateSelectionButtons, hasDeviceSelection,
        scheduleAutosave: runtime.scheduleAutosave,
        deleteSelectedTemplate: libraryController.deleteSelectedTemplate
    });
    
    // Instance layout controller
    const instanceLayoutController = initInstanceLayoutController({
        instanceModel, definitionModel, renderDevice: runtime.renderDevice,
        renderAllCables, updateConnectedPorts, recordAction: runtime.recordAction,
        scheduleAutosave: runtime.scheduleAutosave,
        getTemplateForDevice: runtime.getTemplateForDevice,
        getGroupDisplayName: runtime.getGroupDisplayName
    });
    
    // Share controller
    const shareController = initShareController({
        getDefinitions: () => definitionModel.getAll(),
        getInstances: () => instanceModel.getAll(),
        getConnections: () => connectionModel.getAll(),
        getSelectedDeviceIds: () => selectionState.devices,
        applyPatchData: runtime.applyPatchData,
        clearState: () => {
            definitionModel.clear();
            instanceModel.clear();
            connectionModel.clear();
            clearDeviceSelection();
            state.selectedConnectionId = null;
            state.selectedTemplateId = null;
            runtime.clearHistory?.();
            canvas.innerHTML = '';
        },
        renderDevice: runtime.renderDevice, renderAllCables, updateConnectedPorts,
        renderLibrary: () => runtime.renderLibrary?.(), autosave: runtime.autosave,
        addDefinition: (t) => definitionModel.add(t),
        addInstance: (d) => instanceModel.add(d),
        addConnection: (c) => connectionModel.add(c)
    });
    
    // Patch modal controller
    const patchModalController = initPatchModalController({
        storageService, recentPatchesService,
        getCurrentPatchName: () => appState.get('patch.name'),
        setCurrentPatchName: runtime.setCurrentPatchName,
        getPatchSettings: () => patchSettingsService.getRawSettings(),
        setPatchSettings: (settings) => {
            if (settings.cableStyle !== undefined) {
                patchSettingsService.setSetting('cableStyle', settings.cableStyle);
            }
        },
        createNewPatch: runtime.createNewPatch, renderAllCables,
        scheduleAutosave: runtime.scheduleAutosave,
        getInstanceCount: () => instanceModel.getCount(),
        getConnectionCount: () => connectionModel.getCount(),
        invalidatePatchesCache: () => patchSwitcherController?.invalidatePatchesCache()
    });
    
    // Patch switcher controller
    const patchSwitcherController = initPatchSwitcherController({
        storageService, recentPatchesService,
        loadAndApplyPatch: runtime.loadAndApplyPatch,
        setCurrentPatchName: runtime.setCurrentPatchName,
        getCurrentPatchName: () => appState.get('patch.name'),
        showConfirmModal,
        getInstanceCount: () => instanceModel.getCount(),
        getConnectionCount: () => connectionModel.getCount(),
        newPatch: runtime.newPatch,
        showSaveAsModal: patchModalController.showSaveAsModal,
        showOpenStorageModal: storageUIController.showOpenStorageModal,
        showSaveVersionModal: versionHistoryController.showSaveVersionModal,
        showVersionHistoryModal: versionHistoryController.showVersionHistoryModal,
        saveState: runtime.saveState,
        importFromFile: runtime.importFromFile,
        showPatchSettingsModal: patchModalController.showPatchSettingsModal
    });
    
    // Update callback for recent patches
    recentPatchesService.setOnCloudLoadedCallback(() => {
        patchSwitcherController.renderPatchSwitcherList?.();
    });
    
    // Device modal controller
    initDeviceModalController({
        instanceModel, definitionModel, connectionModel,
        getTemplateForDevice: runtime.getTemplateForDevice,
        getConnectionSourceGroup, getConnectionTargetGroup,
        createDevice: runtime.createDevice,
        rerenderAllDevicesForTemplate: runtime.rerenderAllDevicesForTemplate,
        renderDevice: runtime.renderDevice, renderAllCables, updateConnectedPorts,
        renderLibrary: () => runtime.renderLibrary?.(),
        scheduleAutosave: runtime.scheduleAutosave, recordAction: runtime.recordAction
    });
    
    // Context menu controller
    initContextMenuController({
        screenToCanvas, hasDeviceSelection, getSelectionDevices: () => getSelectedDevices(),
        setDeviceSelection, performCopy, performCut, performPaste, duplicateDevice,
        duplicateSelectedDevice, deleteSelected, deleteConnection, deleteSelectedCables,
        clearRouting, editInstanceLayout: runtime.editInstanceLayout,
        editSelectedDevice: runtime.editDefinition,
        enterTouchMultiSelectMode: () => {},
        selectAllDevices: () => setDeviceSelection(instanceModel.map(d => d.id)),
        showAddDeviceModal, duplicateTemplate: libraryController.duplicateTemplate,
        deleteTemplate: libraryController.deleteTemplate,
        getSelectedTemplateId: () => state.selectedTemplateId,
        setSelectedTemplateId: (id) => { state.selectedTemplateId = id; },
        getClipboardState
    });
    
    // Keyboard controller
    initKeyboardController({
        onAltKeyChange: (isPressed, e) => {
            const cableDragState = getCableDragState();
            if (cableDragState?.isMultiDrag) e.preventDefault();
            runtime.refreshMultiDragPreview?.(isPressed);
        }
    });
    
    // =========================================================================
    // Phase 6: Keyboard Shortcuts
    // =========================================================================
    
    initKeyboardShortcuts({
        undo: runtime.undo,
        redo: runtime.redo,
        selectAll: () => {
            if (state.selectedTemplateId || libraryMultiSelect.selectedItems.size > 0) {
                const { selectAllLibraryItems } = require('./controllers/SelectionController.js');
                selectAllLibraryItems();
                showToast(`Selected ${libraryMultiSelect.selectedItems.size} devices`);
            } else if (instanceModel.getCount() > 0) {
                setDeviceSelection(instanceModel.map(d => d.id));
                runtime.updateSelectionButtons?.();
                showToast(`Selected ${instanceModel.getCount()} devices`);
            }
        },
        copy: () => copySelectedToClipboard(),
        cut: () => performCut(),
        paste: () => performPaste(),
        duplicate: () => runtime.handleDuplicate?.(),
        delete: () => {
            if (libraryMultiSelect.selectedItems.size > 0 || state.selectedTemplateId) {
                libraryController.deleteSelectedTemplate();
            } else {
                deleteSelected();
            }
        },
        deleteWithShift: clearRouting,
        escape: () => {
            const helpOverlay = document.getElementById('help-overlay');
            if (helpOverlay?.classList.contains('active')) {
                helpOverlay.classList.remove('active');
                return;
            }
            closeModal();
            runtime.selectDevice?.(null);
            libraryMultiSelect.selectedItems.clear();
            libraryMultiSelect.lastClickedId = null;
            clearMultiSelection();
            clearBoxSelection();
            runtime.renderLibrary?.();
        },
        zoomIn: () => {
            const container = document.querySelector('.canvas-container');
            const rect = container.getBoundingClientRect();
            zoomAtPoint(rect.left + rect.width/2, rect.top + rect.height/2, getViewport().scale * 1.2);
            updateZoomIndicator();
            saveViewport();
        },
        zoomOut: () => {
            const container = document.querySelector('.canvas-container');
            const rect = container.getBoundingClientRect();
            zoomAtPoint(rect.left + rect.width/2, rect.top + rect.height/2, getViewport().scale / 1.2);
            updateZoomIndicator();
            saveViewport();
        },
        resetZoom: () => { resetZoom(); saveViewport(); },
        toggleLibrary: libraryController.toggleLibrary,
        editDefinition: runtime.editDefinition,
        editInstanceLayout: runtime.editInstanceLayout,
        save: runtime.saveState,
        open: runtime.loadState,
        newPatch: runtime.newPatch,
        openSettings: patchModalController.showPatchSettingsModal,
        toggleHelp: runtime.toggleHelpOverlay
    });
    
    // =========================================================================
    // Phase 7: Event Listeners
    // =========================================================================
    
    // Wheel zoom on canvas container
    const canvasContainer = document.querySelector('.canvas-container');
    canvasContainer.addEventListener('wheel', handleWheel, { passive: false });
    
    // Alt+WASD port group layout shortcut
    document.addEventListener('click', (e) => {
        const heldWasdKey = getHeldWasdKey();
        if (!e.altKey || !heldWasdKey) return;
        
        const newSide = WASD_SIDE_MAP[heldWasdKey];
        if (!newSide) return;
        
        const portGroupEl = e.target.closest('.port-group-header') || e.target.closest('.port-group-folded');
        if (!portGroupEl) return;
        
        const deviceId = portGroupEl.dataset.device;
        const foldKey = portGroupEl.dataset.foldKey || portGroupEl.dataset.group;
        if (!deviceId || !foldKey) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        if (instanceLayoutController.setDeviceGroupSide(deviceId, foldKey, newSide)) {
            const sideNames = { top: 'Top', left: 'Left', bottom: 'Bottom', right: 'Right' };
            showToast(`Group moved to ${sideNames[newSide]}`);
        }
    }, true);
    
    // =========================================================================
    // Phase 8: Final Setup
    // =========================================================================
    
    // Set document title and version indicator
    document.title = "Patchwerk v" + APP_VERSION;
    const versionEl = document.querySelector('.version-indicator');
    if (versionEl) {
        versionEl.textContent = "v" + APP_VERSION;
        if (BUILD_TIMESTAMP) versionEl.title = `Build: ${BUILD_TIMESTAMP}`;
    }
    
    // Initialize auth
    await authService.init();
    appState.set('auth.user', authService.getCurrentUser());
    
    // Cloud sync on login
    if (authService.isLoggedIn()) {
        await syncUserDataFromCloud();
    }
    
    // Load debug settings
    loadDebugSettings((rules) => { PORT_CONNECTION_RULES = rules; });
    
    // Load viewport and apply
    loadViewport();
    applyViewportTransform();
    updateZoomIndicator();
    
    // Load favorites
    favoritesService.load();
    
    // Load current patch name
    try {
        const saved = localStorage.getItem(CURRENT_PATCH_STORAGE_KEY);
        if (saved) {
            appState.set('patch.name', saved);
            const display = document.getElementById('patch-name-display');
            if (display) display.textContent = saved;
        }
    } catch (e) {
        console.warn('Failed to load current patch name:', e);
    }
    
    // Set initial library state
    libraryController.setInitialLibraryState();
    
    // Tab focus sync
    let lastFocusSyncTime = 0;
    const FOCUS_SYNC_COOLDOWN_MS = 5000;
    
    window.addEventListener('focus', async () => {
        if (!authService.isLoggedIn()) return;
        
        const now = Date.now();
        if (now - lastFocusSyncTime < FOCUS_SYNC_COOLDOWN_MS) return;
        lastFocusSyncTime = now;
        
        await syncUserDataFromCloud();
    });
    
    console.log('[Bootstrap] Application bootstrap complete');
    
    // =========================================================================
    // Return Controllers and Services for app.js
    // =========================================================================
    
    return {
        // Models
        state,
        instanceModel,
        definitionModel,
        connectionModel,
        debugConfig,
        selectionState,
        libraryMultiSelect,
        PORT_CONNECTION_RULES,
        
        // Services
        authService,
        storageService,
        favoritesService,
        patchSettingsService,
        recentPatchesService,
        
        // Controllers
        authUIController,
        versionHistoryController,
        storageUIController,
        migrationController,
        libraryController,
        instanceLayoutController,
        shareController,
        patchModalController,
        patchSwitcherController,
        
        // DOM refs
        canvas,
        cablesSvg,
        
        // Re-exports for runtime use
        renderAllCables,
        updateConnectedPorts,
        getViewport,
        applyViewportTransform,
        updateZoomIndicator,
        fitAllDevices,
        saveViewport,
        screenToCanvas,
        getPortPosition,
        getConnectionSourceGroup,
        getConnectionTargetGroup,
        clearDeviceSelection,
        setDeviceSelection,
        hasDeviceSelection,
        getSingleSelectedDevice,
        getSelectedDevices,
        updateSelectionVisuals,
        bringToFront,
        showToast,
        showConfirmModal,
        showAddDeviceModal,
        showEditTemplateModal,
        closeModal,
        getCableDragState,
        getLastMousePosition,
        getLastHoveredTargetPortInfo,
        updateTempCable,
        deleteSelected,
        clearRouting,
        copySelection,
        pasteSelection,
        getSelectionClipboard,
        hasSelectionClipboard,
        copyDeviceToSystemClipboard,
        copyLibrarySelectionToClipboard,
        pasteDeviceFromSystemClipboard,
        copySelectionToSystemClipboard,
        pasteSelectionFromSystemClipboard,
        hasClipboardContent,
        pasteDevice,
        duplicateDevice,
        // Button handlers
        copySelectedToClipboard,
        pasteFromClipboard,
        duplicateSelectedDevice,
        exportSelectedDevice,
        importDeviceFromFile,
        toggleAccountMenu,
        togglePatchSwitcher,
        closePatchSwitcher,
        toggleDebugPanel,
        updateDebugStraightDist,
        updateDebugCableStyle,
        updatePortRule,
        deletePortType,
        exportDebugSettings,
        contextMenuAction
    };
}

export { Container };
export default { Container, container, bootstrap };
